import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col, Tooltip } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import PropTypes from 'prop-types';

import ProfileCircle from '../common/profile/ProfileCircle';

import { getTextColor } from '../schedule/scheduleHelpers';

import { getIdMap } from '../helpers/helpers';
import { initialsFromName } from '../helpers/users';

const getWorkCompletionTitle = (name, completed, notAssigned) => {
  const completedText = completed ? 'Completed' : 'Incomplete';
  const assignedText = notAssigned ? ' (Not Assigned)' : '';
  return `${name} - ${completedText}${assignedText}`;
};

export default function BoardCardUsers({
  users: cardUsers = [],
  workCompletionUsers = [],
  enableWorkCompletion,
  style = {},
  justify = 'end',
  tooltipPlacement = 'right',
}) {
  const users = useSelector((state) => state.users.users);

  const userMap = useMemo(() => getIdMap(users), [users]);

  const usersToUse = useMemo(() => (
    enableWorkCompletion ? workCompletionUsers : cardUsers.map((userId) => ({ userId }))
  ), [enableWorkCompletion, workCompletionUsers, cardUsers]);

  const userView = useMemo(() => (
    usersToUse.map((user) => {
      const {
        userId: assigneeId,
        completed,
        notAssigned,
      } = user;
      const {
        [assigneeId]: {
          name = '',
          scheduleColor,
        } = {},
      } = userMap;
      if (!name) return null;

      const initials = initialsFromName(name);

      const circleStyle = scheduleColor
        ? { backgroundColor: scheduleColor }
        : {};

      const textColor = scheduleColor ? getTextColor(scheduleColor) : undefined;

      const containerStyle = notAssigned && enableWorkCompletion ? { opacity: 0.3 } : {};

      const title = enableWorkCompletion
        ? getWorkCompletionTitle(name, completed, notAssigned)
        : name;

      return (
        <Col key={assigneeId}>
          <Tooltip title={title} placement={tooltipPlacement}>
            <div style={{ position: 'relative', ...containerStyle }}>
              <ProfileCircle
                initials={initials}
                radius={10}
                className="boards-card-profile-circle"
                style={circleStyle}
                accentColor={textColor}
              />
              {!!(completed && enableWorkCompletion) && (
                <CheckCircleFilled
                  style={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    fontSize: 12,
                    color: '#52c41a',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    zIndex: 1,
                  }}
                />
              )}
            </div>
          </Tooltip>
        </Col>
      );
    })
  ), [tooltipPlacement, usersToUse, userMap, enableWorkCompletion]);

  return (
    <Row style={{ maxWidth: '25%', overflow: 'hidden', ...style }} justify={justify} gutter={5}>
      {userView}
    </Row>
  );
}

/* eslint-disable react/forbid-prop-types */
BoardCardUsers.propTypes = {
  users: PropTypes.array,
  workCompletionUsers: PropTypes.array,
  enableWorkCompletion: PropTypes.bool,
  style: PropTypes.object,
  justify: PropTypes.string,
  tooltipPlacement: PropTypes.string,
};

BoardCardUsers.defaultProps = {
  users: [],
  workCompletionUsers: [],
  enableWorkCompletion: false,
  style: {},
  justify: 'end',
  tooltipPlacement: 'right',
};
